import { useMemo } from 'react';
import { Check, X } from 'lucide-react';

function getPasswordStrength(password: string) {
  const checks = {
    minLength: password.length >= 6,
    hasUpper: /[A-Z]/.test(password),
    hasLower: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };
  const score = Object.values(checks).filter(Boolean).length;
  const label = score <= 1 ? 'Muito fraca' : score === 2 ? 'Fraca' : score === 3 ? 'Média' : score === 4 ? 'Forte' : 'Muito forte';
  const color = score <= 1 ? 'bg-destructive' : score === 2 ? 'bg-[hsl(36,90%,52%)]' : score === 3 ? 'bg-[hsl(36,90%,52%)]' : score === 4 ? 'bg-secondary' : 'bg-[hsl(158,55%,42%)]';
  return { checks, score, label, color };
}

function Requirement({ met, text }: { met: boolean; text: string }) {
  return (
    <div className={`flex items-center gap-2 text-xs transition-colors ${met ? 'text-[hsl(158,55%,50%)]' : 'text-[hsl(220,15%,45%)]'}`}>
      {met ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
      {text}
    </div>
  );
}

export function PasswordStrengthIndicator({ password }: { password: string }) {
  const strength = useMemo(() => getPasswordStrength(password), [password]);

  if (password.length === 0) return null;

  return (
    <div className="space-y-2.5 animate-fade-in">
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 rounded-full bg-[hsl(222,30%,20%)] overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${strength.color}`}
            style={{ width: `${(strength.score / 5) * 100}%` }}
          />
        </div>
        <span className={`text-xs font-medium min-w-[80px] text-right ${
          strength.score <= 2 ? 'text-destructive' : strength.score <= 3 ? 'text-[hsl(36,90%,52%)]' : 'text-[hsl(158,55%,50%)]'
        }`}>
          {strength.label}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <Requirement met={strength.checks.minLength} text="6+ caracteres" />
        <Requirement met={strength.checks.hasUpper} text="Letra maiúscula" />
        <Requirement met={strength.checks.hasLower} text="Letra minúscula" />
        <Requirement met={strength.checks.hasNumber} text="Número" />
        <Requirement met={strength.checks.hasSpecial} text="Caractere especial" />
      </div>
    </div>
  );
}
